"use client";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import PlayButton from "./play-button";
import Image from "next/image";
import PauseButton from "./pause-button";

export function VideoComponent({ thumbnail, videoUrl }: { thumbnail?: string; videoUrl: string }) {
  const playerRef = useRef<HTMLVideoElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const isImage = !videoUrl && !!thumbnail;

  useEffect(() => {
    if (isImage) return;

    const videoElement = playerRef.current;
    if (!videoElement) return;

    const handlePlay = () => setIsVideoPlaying(true);
    const handlePause = () => setIsVideoPlaying(false);
    const handleLoadedData = () => {
      setIsVideoLoaded(true);
    };

    videoElement.addEventListener("play", handlePlay);
    videoElement.addEventListener("pause", handlePause);
    videoElement.addEventListener("loadeddata", handleLoadedData);

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          videoElement.play();
        } else {
          videoElement.pause();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current.observe(videoElement);

    return () => {
      if (observerRef.current && videoElement) {
        observerRef.current.unobserve(videoElement);
      }

      videoElement.removeEventListener("play", handlePlay);
      videoElement.removeEventListener("pause", handlePause);
      videoElement.removeEventListener("loadeddata", handleLoadedData);
    };
  }, [isImage]);

  const handlePlayPause = () => {
    const videoElement = playerRef.current;
    if (!videoElement) return;

    if (isVideoPlaying) {
      videoElement.pause();
    } else {
      setIsHovered(false);
      videoElement.play();
    }
  };

  // If it's an image, return only the image content
  if (isImage) {
    return (
      <div className="relative" style={{ width: "100%", height: "100%" }}>
        <Image
          fill
          src={thumbnail!}
          alt="Image thumbnail"
          className="absolute inset-0 z-[1] object-cover"
        />
      </div>
    );
  }

  // Otherwise, handle video content with thumbnail loading logic
  return (
    <div
      className="relative"
      style={{ width: "100%", height: "100%" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {thumbnail && !isVideoLoaded && (
        <>
          <Image
            fill
            src={thumbnail}
            alt="Video thumbnail"
            className="absolute inset-0 z-[1] object-cover"
          />
          <div className="absolute inset-0 z-[2] flex items-center justify-center bg-black bg-opacity-50">
            <div className="custom-spin h-16 w-16 rounded-full border-4 border-white border-t-transparent"></div>
          </div>
        </>
      )}
      <video
        ref={playerRef}
        src={videoUrl}
        onTouchStart={() => setIsHovered(!isHovered)}
        controls
        loop
        preload="none"
        className="aspect-video h-full w-full object-cover"
        poster={thumbnail}
      />

      {!isVideoPlaying && (
        <div
          className={cn(
            "z-[90] flex items-center justify-center",
            "absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]"
          )}
          onClick={handlePlayPause}
        >
          <PlayButton />
        </div>
      )}
      {isHovered && isVideoPlaying && (
        <div
          className={cn(
            "z-[90] flex items-center justify-center",
            "absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]"
          )}
          onClick={handlePlayPause}
        >
          <PauseButton />
        </div>
      )}
    </div>
  );
}
